/**
 * ReceivingService — BQC4 receiving-scan event log (iSyn2_ReceivingScans).
 * Pure append + soft-undo, mirrors iSyn2_CutReadyScans exactly (see
 * WorkcentersService's cut-ready block); receivingLedger.ts is what a scan
 * MEANS against a slip. This service never writes iSyn_WIPs — stage
 * authority stays Excel-owned until a later Gate 3 (SCREENS-BQC3.md §7).
 */
import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { readByIdRanges } from "./idRangeRead";
import type { IReceivingScan } from "../../modules/mfg-workcenters/receivingLedger";
import { notifyPortalStale } from "../../modules/sewer-portal/portalStale";

export type { IReceivingScan };

const RECEIVING_LIST = "iSyn2_ReceivingScans";

/** Columns every live list already has. `repairReturn` and
 * `syncedToWorkbookAt` were each added later (Isyn2ProvisioningService's
 * addMissingFields), in that order, and either may not exist yet on a list
 * provisioned before it — SharePoint 400s the WHOLE $select if even one
 * named column is missing, so reads try the fullest select first and fall
 * back a generation at a time on failure (see listReceivingScans). */
const RECEIVING_CORE = "Id,wipId,dsNumber,qty,receivedBy,receivedAt,undoneBy,undoneAt";
const RECEIVING_WITH_REPAIR_RETURN = `${RECEIVING_CORE},repairReturn`;
const RECEIVING_SELECT = `${RECEIVING_WITH_REPAIR_RETURN},syncedToWorkbookAt`;

export class ReceivingService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);
  private types = new Map<string, string>();

  private async type(list: string): Promise<string> {
    const hit = this.types.get(list);
    if (hit) return hit;
    const r = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${list}')?$select=ListItemEntityTypeFullName`);
    this.types.set(list, r.d.ListItemEntityTypeFullName);
    return r.d.ListItemEntityTypeFullName;
  }

  private mapScan(r: Record<string, unknown>): IReceivingScan {
    return {
      spId: r.Id as number,
      wipId: String(r.wipId ?? "").trim(),
      dsNumber: String(r.dsNumber ?? ""),
      qty: Number(r.qty ?? 0),
      receivedBy: String(r.receivedBy ?? ""),
      receivedAt: String(r.receivedAt ?? ""),
      undoneBy: r.undoneBy ? String(r.undoneBy) : undefined,
      undoneAt: r.undoneAt ? String(r.undoneAt) : undefined,
      ...(r.repairReturn === true ? { repairReturn: true } : {}),
      ...(r.syncedToWorkbookAt ? { syncedToWorkbookAt: String(r.syncedToWorkbookAt) } : {}),
    };
  }

  /** See QcService.rangedReadBroken. */
  private rangedReadBroken = false;

  /**
   * One read of the receiving ledger, in parallel Id ranges when it can — the
   * same change QcService.read makes, for the same reason (BQC5 reads this list
   * on every open alongside the QC ledger, and BQC4 and payroll read it too).
   * A small list is one range and so one request, no slower than before; any
   * failure falls back to the ordinary pager, which is also what reproduces a
   * missing-column error for the generation chain in `listReceivingScans`.
   */
  private async read(select: string): Promise<IReceivingScan[]> {
    let rangedFailed = false;
    if (!this.rangedReadBroken) {
      try {
        const rows = await readByIdRanges(
          q => this.sp.getListItems<Record<string, unknown>>(RECEIVING_LIST, q), select);
        return rows.map(r => this.mapScan(r));
      } catch {
        rangedFailed = true;
      }
    }
    const rows = await this.sp.getListItems<Record<string, unknown>>(RECEIVING_LIST, { select });
    if (rangedFailed) this.rangedReadBroken = true;
    return rows.map(r => this.mapScan(r));
  }

  /** One WIP's receiving rows only, fresh — a filtered query, not the whole ledger. Throws if the
   * list cannot filter on wipId; the caller falls back to the full read. */
  public async listReceivingScansForWip(wip: string): Promise<IReceivingScan[]> {
    const rows = await this.sp.getListItems<Record<string, unknown>>(RECEIVING_LIST, {
      select: RECEIVING_WITH_REPAIR_RETURN, filter: `wipId eq '${wip.replace(/'/g, "''")}'`, top: 500,
    });
    return rows.map(r => this.mapScan(r));
  }

  /** Every scan row, undone ones included — the caller reduces to active. */
  public async listReceivingScans(): Promise<IReceivingScan[]> {
    try {
      return await this.read(RECEIVING_SELECT);
    } catch (e) {
      console.warn("[ReceivingService] scan read without syncedToWorkbookAt column — use \"Add missing columns\" on the Migration Valve to add it.", e);
    }
    try {
      return await this.read(RECEIVING_WITH_REPAIR_RETURN);
    } catch (e) {
      console.warn("[ReceivingService] scan read without repairReturn column either — use \"Add missing columns\" on the Migration Valve to add it.", e);
      return this.read(RECEIVING_CORE);
    }
  }

  /** Records one receiving scan and returns it with its list id, so the
   * station can show the row (and undo it) without a full reload.
   * `repairReturn` only gets attempted when true — the common case never
   * touches the column at all, so a list this hasn't been provisioned on
   * yet is unaffected for every ordinary receipt; only an actual repair
   * return risks the fallback below. */
  public async createReceivingScan(
    row: { wipId: string; dsNumber?: string; qty?: number; repairReturn?: boolean },
    receivedBy: string
  ): Promise<IReceivingScan> {
    const type = await this.type(RECEIVING_LIST);
    const receivedAt = new Date().toISOString();
    const core = {
      __metadata: { type },
      Title: row.wipId,
      wipId: row.wipId,
      dsNumber: row.dsNumber ?? "",
      qty: row.qty ?? null,
      receivedBy,
      receivedAt,
    };
    let repairReturnWritten = false;
    let created: { Id: number };
    if (row.repairReturn) {
      try {
        created = await this.sp.createItem<{ Id: number }>(RECEIVING_LIST, { ...core, repairReturn: true });
        repairReturnWritten = true;
      } catch (e) {
        console.warn("[ReceivingService] could not tag scan as a repair return — repairReturn column missing? Add it via Migration Valve → \"Add missing columns\". Recording the receipt without the tag.", e);
        created = await this.sp.createItem<{ Id: number }>(RECEIVING_LIST, core);
      }
    } else {
      created = await this.sp.createItem<{ Id: number }>(RECEIVING_LIST, core);
    }
    notifyPortalStale();
    return {
      spId: created.Id, wipId: row.wipId, dsNumber: row.dsNumber ?? "",
      qty: row.qty ?? 0, receivedBy, receivedAt,
      ...(repairReturnWritten ? { repairReturn: true } : {}),
    };
  }

  /** Soft-undo a mis-scan. The row keeps existing as history; it just stops
   * counting as received everywhere. */
  public async undoReceivingScan(spId: number, by: string): Promise<void> {
    const type = await this.type(RECEIVING_LIST);
    await this.sp.updateItem(RECEIVING_LIST, spId, {
      __metadata: { type },
      undoneBy: by,
      undoneAt: new Date().toISOString(),
    });
  }

  /** Hard-delete a scan row — the record is GONE, not history. For rows that
   * should never have existed; an ordinary mis-scan wants undoReceivingScan
   * instead. */
  public async deleteReceivingScan(spId: number): Promise<void> {
    await this.sp.deleteItem(RECEIVING_LIST, spId);
  }

  /** Marks scans as exported to the RECEIVING outbox — called once the CSV
   * upload has actually succeeded, never before, so a failed upload leaves
   * every one of them eligible for the next attempt rather than silently
   * skipped. Per-scan, not per-WIP — see IReceivingScan.syncedToWorkbookAt. */
  public async markSyncedToWorkbook(spIds: readonly number[], at: string): Promise<void> {
    const type = await this.type(RECEIVING_LIST);
    for (const spId of spIds) {
      await this.sp.updateItem(RECEIVING_LIST, spId, { __metadata: { type }, syncedToWorkbookAt: at });
    }
  }

  /** Hard-deletes every scan tagged a repair return (any state, undone
   * included) — a full wipe of the Return-to-Sewer round trip's BQC4 half,
   * for clearing out rehearsal data before a fresh test. Ordinary receiving
   * scans are untouched. Returns how many rows were removed. */
  public async clearRepairReturnScans(): Promise<number> {
    const rows = await this.listReceivingScans();
    const ids = rows.filter(r => r.repairReturn).map(r => r.spId);
    for (const id of ids) await this.deleteReceivingScan(id);
    return ids.length;
  }
}

export const receivingService = new ReceivingService();
